import { ref } from 'vue';
import SockJS from 'sockjs-client/dist/sockjs';
import Stomp from 'stompjs';
import useAuthStore from '@/store/modules/auth.js';
import { parseJsonString } from '@/helpers/common.js';

const useSocketHook = (path, initialSubscribedChannels = {}) => {
  const authStore = useAuthStore();

  const stompClient = ref(null);
  const subscribedChannels = ref(initialSubscribedChannels);
  const connectTimeoutIdList = ref([]);

  const createStompClient = () => {
    if (!stompClient.value?.connected) {
      const newSocket = new SockJS(path);
      stompClient.value = Stomp.over(newSocket);

      // Disable the next line to enable debug logs for development
      stompClient.value.debug = null;
    }
  };

  const removeStompClient = () => new Promise((resolve) => {
    if (stompClient.value === null) {
      resolve('Stomp client is already disconnected');
    } else {
      stompClient.value.disconnect(() => {
        stompClient.value = null;
        resolve('Stomp client disconnected');
      });
    }
  });

  const connectStompClient = (successCallback) => {
    const clearTimeouts = () => {
      if (connectTimeoutIdList.value.length > 0) {
        connectTimeoutIdList.value.forEach((timeoutId) => {
          clearTimeout(timeoutId);
        });

        connectTimeoutIdList.value = [];
      }
    };

    const connect = () => {
      stompClient.value.connect(
        { Authorization: authStore.jwtToken },
        () => {
          clearTimeouts();

          successCallback();
        },
        (_e) => {
          const timeoutId = setTimeout(async () => {
            clearTimeouts();

            await removeStompClient();

            createStompClient();

            connect();
          }, 3000);

          connectTimeoutIdList.value.push(timeoutId);
        },
      );
    };

    connect();
  };

  const subscribeToChannel = (channelKey, channelPath, successCallback, errorCallback) => {
    if (!Object.hasOwn(subscribedChannels.value, channelKey)) { return; }

    subscribedChannels.value[channelKey] = stompClient.value.subscribe(
      channelPath,
      (message) => {
        const parsedMessage = parseJsonString(message.body);

        successCallback(parsedMessage);
      },
      (error) => {
        if (errorCallback) {
          errorCallback(error);
        }
      });
  };

  const unsubscribeFromChannel = (channelKey) => {
    if (!subscribedChannels.value[channelKey]) { return; }

    subscribedChannels.value[channelKey].unsubscribe();

    subscribedChannels.value[channelKey] = null;
  };

  return {
    stompClient,
    subscribedChannels,
    createStompClient,
    removeStompClient,
    connectStompClient,
    subscribeToChannel,
    unsubscribeFromChannel,
  };
};

export default useSocketHook;
